/**
 * Wspólny czytnik strumieni SSE.
 *
 * Powstał, gdy trzeci konsument (asystent AI) miałby być trzecią kopią tej samej pętli —
 * wcześniej dwie identyczne wersje żyły w `endpoints.ts` przy mapach i auto-generacji oceny
 * ryzyka. Parsowanie SSE ma kilka miejsc, w których łatwo o cichy błąd (sklejanie wieloliniowego
 * `data:`, event bez nazwy, ramka rozcięta między porcjami strumienia), więc jedna implementacja
 * jest tu warta więcej niż samo skrócenie kodu.
 *
 * `EventSource` odpada: obsługuje wyłącznie GET, a wszystkie nasze strumienie to POST z ciałem.
 */

export interface SseHandlers {
  /** Wołane dla każdego zdarzenia. `event` to nazwa z pola `event:` (domyślnie „message"). */
  [event: string]: (data: string) => void;
}

export interface SseOptions {
  method?: string;
  body?: unknown;
  /** Wołane dla zdarzeń bez zarejestrowanej obsługi — do diagnostyki. */
  onUnknown?: (event: string, data: string) => void;
  /**
   * Nazwa operacji doklejana do komunikatu błędu HTTP. Bez niej „Brak dostępu" trafia do paska
   * błędu w oderwaniu od tego, co właściwie padło — bezużyteczne w zgłoszeniu serwisowym.
   */
  kontekst?: string;
  /** Przerwanie strumienia — patrz `AbortController` w konsumentach. */
  signal?: AbortSignal;
}

/**
 * Otwiera strumień i woła handlery aż do jego zamknięcia.
 *
 * Zdarzenie `error` NIE jest obsługiwane automatycznie — różne strumienie znaczą przez nie
 * co innego (jedne przerywają, inne raportują ostrzeżenie), więc decyzję zostawiamy wołającemu.
 */
export async function czytajSse(url: string, handlers: SseHandlers, opts: SseOptions = {}): Promise<void> {
  const r = await fetch(url, {
    method: opts.method ?? 'POST',
    credentials: 'include',
    headers: {
      Accept: 'text/event-stream',
      ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });

  if (!r.ok || !r.body) {
    const text = await r.text().catch(() => '');
    // Treść błędu bywa JSON-em z polem `message` — pokazujemy ją, zamiast samego kodu.
    let opis = text || r.statusText;
    try {
      const j = JSON.parse(text);
      if (j?.message) opis = j.message;
    } catch { /* nie JSON — zostaje surowy tekst */ }
    // Kod statusu zostaje ZAWSZE, nie tylko gdy brakuje opisu: bez niego 403 i 500 wyglądają
    // dla zgłaszającego identycznie.
    const czolo = opts.kontekst ? `${opts.kontekst} — ` : '';
    throw new Error(`${czolo}błąd ${r.status}${opis ? `: ${opis}` : ''}`);
  }

  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    // stream: true — ramka może zostać rozcięta w środku znaku wielobajtowego.
    buf += decoder.decode(value, { stream: true });

    let split;
    while ((split = buf.indexOf('\n\n')) !== -1) {
      const ramka = buf.slice(0, split);
      buf = buf.slice(split + 2);

      let event = 'message';
      let data = '';
      for (const line of ramka.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        // Wieloliniowe `data:` skleja się znakiem nowej linii — tak mówi specyfikacja SSE
        // i tak wysyła Spring, gdy w danych są znaki nowej linii.
        //
        // BEZ trimStart: Spring zapisuje pole jako `data:` + ładunek, bez spacji-separatora, więc
        // każdy biały znak po dwukropku NALEŻY do danych. Obcinanie go sklejało strumieniowaną
        // odpowiedź modelu w jeden ciąg słów i zjadało wcięcia list.
        else if (line.startsWith('data:')) data += (data ? '\n' : '') + line.slice(5);
      }

      const handler = handlers[event];
      if (handler) handler(data);
      else opts.onUnknown?.(event, data);
    }
  }
}

/** Wygodne opakowanie: parsuje `data` jako JSON i pomija ramki nieparsowalne. */
export function json<T>(fn: (v: T) => void): (data: string) => void {
  return (data: string) => {
    try {
      fn(JSON.parse(data) as T);
    } catch {
      /* zniekształcona ramka — pomijamy zamiast wywracać cały strumień */
    }
  };
}

/**
 * Wariant dla strumieni, które kończą się jednym zdarzeniem `done` z wynikiem — czyli dla
 * wszystkich naszych.
 *
 * Istnieje też po to, żeby asercja typu była w JEDNYM miejscu. TypeScript nie śledzi przypisań
 * wykonanych w callbacku, więc po `if (!wynik) throw` zawęża zmienną do `never` i każde użycie
 * pola zgłasza błąd. Zamiast rozsypywać `as T` po wywołaniach, trzymamy to tutaj, z wyjaśnieniem.
 */
export async function strumienZWynikiem<T>(
  url: string,
  handlers: SseHandlers = {},
  opts: SseOptions = {},
): Promise<T> {
  let wynik: T | null = null;
  let blad: string | null = null;

  await czytajSse(url, {
    ...handlers,
    // Handlery wołającego dla `done`/`error` NIE giną: przejmujemy te zdarzenia (na tym polega
    // ta funkcja), ale po swojej robocie oddajemy sterowanie dalej. Sam spread cicho je nadpisywał
    // i callback przekazany przez wołającego po prostu milczał.
    done: (d) => { json<T>(v => { wynik = v; })(d); handlers.done?.(d); },
    error: (d) => {
      // Ładunek bywa JSON-em z polem `message` — inaczej użytkownik dostaje w pasku błędu
      // klamerkę z JSON-em zamiast zdania.
      try { blad = (JSON.parse(d) as { message?: string })?.message ?? d; } catch { blad = d; }
      handlers.error?.(d);
    },
  }, opts);

  if (blad) throw new Error(blad);
  if (wynik === null) throw new Error('Strumień zakończony bez zdarzenia „done".');
  return wynik as T;   // patrz komentarz wyżej — przypisanie następuje w callbacku
}
